import {skipToken} from '@reduxjs/toolkit/query';
import {
  Card,
  Checkbox,
  Choice,
  FormField,
  Separator,
  Switch,
  type FormControl,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Show, Space, Text, VStack} from 'platform/foundation';

import {useWatch, type UseFormReturn} from 'react-hook-form';

import {isEmpty, isNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchQuery} from '@dms/api/core';
import {useGetTenantRegisterListQuery} from '@dms/api/finance';
import {normalizeCheckoutProfileDraft, type CheckoutProfilePaymentMethod} from '@dms/api/sales';
import i18n from '@dms/i18n';

import type {CheckoutProfileFormValues} from '../types/CheckoutProfileFormValuesSchema';
import {checkoutProfileOptions} from '../utils/checkoutProfileOptions';

interface Props {
  control: FormControl<CheckoutProfileFormValues>;
  formApi: UseFormReturn<CheckoutProfileFormValues>;
  currency?: string;
  fieldErrors?: Record<string, string>;
}

export function CheckoutProfilePayments(props: Props) {
  const branchId = useWatch({control: props.control, name: 'branchId'});
  const methods = useWatch({control: props.control, name: 'payments.methods'}) ?? [];
  const defaultMethod = useWatch({control: props.control, name: 'payments.defaultMethod'});
  const {data: branch} = useGetBranchQuery(isNotNilOrEmpty(branchId) ? {branchId} : skipToken);
  const {data: cashRegisters, isLoading} = useGetTenantRegisterListQuery(
    {inactive: false, currency: props.currency},
    {skip: isNilOrEmpty(branchId)}
  );

  const accounts =
    branch?.billingInformation?.bankAccounts.filter(
      (account) =>
        (isNil(account.domains) || isEmpty(account.domains) || account.domains.includes('AFTS')) &&
        (isNilOrEmpty(props.currency) || account.currency === props.currency) &&
        (isNotNilOrEmpty(account.iban) || isNotNilOrEmpty(account.accountNumber))
    ) ?? [];

  const options = checkoutProfileOptions.payments();
  const defaultMethodLabel = i18n.t('entity.order.labels.defaultPaymentMethod');
  const allowMethodLabels = {
    CASH: i18n.t('entity.checkout.labels.allowCash'),
    CARD: i18n.t('entity.checkout.labels.allowCard'),
    BANK_TRANSFER: i18n.t('entity.customerPaymentSettings.labels.allowBankTransfer'),
    CASH_ON_DELIVERY: i18n.t('entity.checkout.labels.allowCashOnDelivery'),
  };

  const toggleMethod = (method: CheckoutProfilePaymentMethod, enabled: boolean) => {
    props.formApi.setValue(
      'payments.methods',
      enabled
        ? [...methods, {method, isAutoInitiatePayment: false}]
        : methods.filter((item) => item.method !== method),
      {shouldDirty: true}
    );

    const normalized = normalizeCheckoutProfileDraft(props.formApi.getValues());
    props.formApi.setValue('payments.defaultMethod', normalized.payments?.defaultMethod ?? null, {
      shouldDirty: true,
    });

    props.formApi.setValue('deposit', normalized.deposit, {shouldDirty: true});
    props.formApi.setValue('documentTypes', normalized.documentTypes, {shouldDirty: true});
    props.formApi.setValue('documents', normalized.documents, {shouldDirty: true});
    void props.formApi.trigger(['payments', 'deposit', 'documentTypes', 'documents']);
  };

  return (
    <VStack>
      <Heading size={4}>{i18n.t('entity.checkout.labels.paymentMethod')}</Heading>
      <Space vertical={1} />
      <Text size="small" color="secondary">
        {i18n.t('entity.checkout.labels.paymentMethodsDescription')}
      </Text>
      <Space vertical={4} />
      <Box id="checkout-focus-payments.isMethodChangeAllowed" tabIndex={-1}>
        <FormField
          control={props.control}
          name="payments.isMethodChangeAllowed"
          errorMessage={props.fieldErrors?.['payments.isMethodChangeAllowed']}
          type="switch"
          label={i18n.t('entity.checkout.labels.allowPaymentMethodChange')}
        />
      </Box>
      <Space vertical={4} />
      <Box id="checkout-focus-payments.methods" tabIndex={-1}>
        <VStack spacing={4}>
          {options.map((option) => {
            const index = methods.findIndex((item) => item.method === option.value);
            const payment = methods[index];
            const allowMethodLabel = allowMethodLabels[option.value];

            return (
              <Card key={option.value} variant="inlineGrey">
                <HStack align="center" justify="space-between" spacing={4}>
                  <HStack align="center" spacing={3}>
                    <Switch
                      name="payments.methods"
                      errorMessage={props.fieldErrors?.['payments.methods']}
                      aria-label={allowMethodLabel}
                      isDisabled={props.formApi.formState.isSubmitting}
                      value={index >= 0}
                      onChange={(enabled) => toggleMethod(option.value, enabled)}
                    />
                    <Heading size={4}>{allowMethodLabel}</Heading>
                  </HStack>
                  <Box id={`checkout-focus-payments.defaultMethod.${option.value}`} tabIndex={-1}>
                    <Checkbox
                      name="payments.defaultMethod"
                      errorMessage={props.fieldErrors?.['payments.defaultMethod']}
                      aria-label={`${defaultMethodLabel}: ${option.label}`}
                      label={defaultMethodLabel}
                      isDisabled={index < 0 || props.formApi.formState.isSubmitting}
                      value={defaultMethod === option.value}
                      onChange={(enabled) =>
                        props.formApi.setValue(
                          'payments.defaultMethod',
                          enabled ? option.value : null,
                          {
                            shouldDirty: true,
                          }
                        )
                      }
                    />
                  </Box>
                </HStack>
                <Show when={payment && payment.method !== 'CASH_ON_DELIVERY' ? payment : null}>
                  {(payment) => (
                    <>
                      <Separator />
                      <Show when={payment.method === 'CASH'}>
                        <Grid columns={4} spacing={4}>
                          <GridItem span={4}>
                            <FormField
                              control={props.control}
                              name={`payments.methods.${index}.cashRegisterId`}
                              errorMessage={
                                props.fieldErrors?.[`payments.methods.${index}.cashRegisterId`]
                              }
                              type="choice"
                              isRequired
                              label={i18n.t('entity.checkout.labels.defaultCashRegister')}
                              options={(cashRegisters ?? [])
                                .filter((item) => item.branchId === branchId)
                                .map((item) => ({value: item.id, label: item.name}))}
                              isLoading={isLoading}
                            />
                          </GridItem>
                        </Grid>
                        <Separator />
                      </Show>
                      <Show when={payment.method === 'CASH' || payment.method === 'CARD'}>
                        <FormField
                          control={props.control}
                          name={`payments.methods.${index}.isAutoInitiatePayment`}
                          errorMessage={
                            props.fieldErrors?.[`payments.methods.${index}.isAutoInitiatePayment`]
                          }
                          type="checkbox"
                          label={i18n.t('entity.settings.labels.initiatePaymentAutomatically')}
                          helperText={i18n.t(
                            'entity.settings.labels.initiatePaymentAutomaticallyHelper'
                          )}
                        />
                      </Show>
                      <Show when={payment.method === 'BANK_TRANSFER' ? payment : null}>
                        {(payment) => (
                          <>
                            <Grid columns={4} spacing={4} align="flex-start">
                              <GridItem span={2}>
                                <Choice
                                  name={`payments.methods.${index}.bankAccount`}
                                  errorMessage={
                                    props.fieldErrors?.[`payments.methods.${index}.bankAccount`] ??
                                    props.fieldErrors?.[
                                      `payments.methods.${index}.bankAccount.iban`
                                    ] ??
                                    props.fieldErrors?.[
                                      `payments.methods.${index}.bankAccount.number`
                                    ] ??
                                    props.formApi.getFieldState(
                                      `payments.methods.${index}.bankAccount`
                                    ).error?.message
                                  }
                                  isRequired
                                  label={i18n.t('entity.checkout.labels.defaultBankAccount')}
                                  isDisabled={props.formApi.formState.isSubmitting}
                                  value={
                                    payment.bankAccount?.iban ?? payment.bankAccount?.number ?? null
                                  }
                                  options={accounts.map((account) => ({
                                    value: account.iban ?? account.accountNumber ?? '',
                                    label:
                                      account.accountName ??
                                      account.iban ??
                                      account.accountNumber ??
                                      '',
                                  }))}
                                  onChange={(value) => {
                                    const account = accounts.find(
                                      (item) => (item.iban ?? item.accountNumber) === value
                                    );

                                    props.formApi.setValue(
                                      `payments.methods.${index}.bankAccount`,
                                      account
                                        ? {
                                            iban: account.iban ?? undefined,
                                            number: account.accountNumber ?? undefined,
                                          }
                                        : undefined,
                                      {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                      }
                                    );
                                  }}
                                />
                              </GridItem>
                              <GridItem span={2}>
                                <FormField
                                  control={props.control}
                                  name={`payments.methods.${index}.maturityDays`}
                                  errorMessage={
                                    props.fieldErrors?.[`payments.methods.${index}.maturityDays`]
                                  }
                                  type="number"
                                  isRequired
                                  decimalPlaces={0}
                                  label={i18n.t(
                                    'entity.checkout.labels.defaultInvoiceMaturityDays'
                                  )}
                                />
                              </GridItem>
                            </Grid>
                            <Separator />
                            <FormField
                              control={props.control}
                              name={`payments.methods.${index}.useCustomerMaturity`}
                              errorMessage={
                                props.fieldErrors?.[`payments.methods.${index}.useCustomerMaturity`]
                              }
                              type="checkbox"
                              label={i18n.t('entity.checkout.labels.useCustomerInvoiceMaturity')}
                              helperText={i18n.t(
                                'entity.checkout.labels.useCustomerInvoiceMaturityDescription'
                              )}
                            />
                          </>
                        )}
                      </Show>
                    </>
                  )}
                </Show>
              </Card>
            );
          })}
        </VStack>
      </Box>
    </VStack>
  );
}
